"use client";

import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { Suspense, useState, useEffect, useRef } from "react";
import Script from 'next/script';
import UtmCaptureHandler from "@/components/UtmCaptureHandler";
import { motion, useScroll, useTransform } from "framer-motion";

// Data for the new interactive features section
const featuresContent = [
  {
    id: 'pr-tracker',
    title: 'PR Tracker',
    description: 'Record and monitor your personal records across different events.',
    imagePlaceholder: 'PR Tracker Visual',
    imageSrc: '/LandingPageImages/PRTracker.png',
  },
  {
    id: 'performance-analytics',
    title: 'Performance Analytics',
    description: 'Visualize your progress, find your best event, and understand your likelihood of competing in each division.',
    imagePlaceholder: 'Analytics Visual',
    imageSrc: '/LandingPageImages/PerformanceAnalysis.png',
  },
  {
    id: 'college-map',
    title: 'College Map',
    description: 'Explore colleges and their athletic programs across divisions.',
    imagePlaceholder: 'College Map Visual',
    imageSrc: '/LandingPageImages/CollegeMap.png',
  },
  {
    id: 'school-tracker',
    title: 'School Tracker',
    description: 'Manage your college contacts and application progress.',
    imagePlaceholder: 'School Tracker Visual',
    imageSrc: '/LandingPageImages/SchoolTracker.png',
  },
];

// New component for the interactive features display
function InteractiveFeaturesDisplay() {
  const [activeIndex, setActiveIndex] = useState(0);
  const featureRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Framer Motion scroll tracking
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Simple intersection observer for section switching
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -20% 0px',
      threshold: 0.5,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = featureRefs.current.findIndex(ref => ref === entry.target);
          if (index !== -1 && index !== activeIndex) {
            setActiveIndex(index);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const currentRefs = featureRefs.current.filter(ref => ref !== null) as HTMLDivElement[];
    currentRefs.forEach(ref => observer.observe(ref));

    return () => {
      currentRefs.forEach(ref => observer.unobserve(ref));
    };
  }, [activeIndex]);

  const currentFeature = featuresContent[activeIndex];


  // NEED TO FIX THIS PART ON MOBILE, CANT SEE IT ON MOBILE
  return (
    <div ref={containerRef} className="relative py-16 md:py-24 pb-2">
      {/* Sticky Panel */}
      <div className="sticky top-20 h-[calc(100vh-5rem)] container mx-auto px-4 hidden md:flex items-center z-10 pointer-events-none">
        {/* Left side: Image placeholder */}
        <div className="w-1/2 lg:w-5/12 p-4 lg:p-8 flex-shrink-0">
          <motion.div 
            className="bg-inherit backdrop-blur-sm aspect-[16/10] rounded-xl shadow-2xl flex items-center justify-center border border-neutral-700"
            key={`container-${activeIndex}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              key={`image-${activeIndex}`}
              className="w-full h-full relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
            >
              <Image
                src={currentFeature?.imageSrc || ''}
                alt={currentFeature?.title || ''}
                fill
                className="object-contain rounded-xl"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </motion.div>
          </motion.div>
        </div>

        {/* Right side: Text content */}
        <motion.div 
          key={`text-container-${activeIndex}`}
          className="w-1/2 lg:w-7/12 p-4 lg:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.h3 
            key={`title-${activeIndex}`}
            className="text-3xl lg:text-4xl font-bold mb-4 lg:mb-6 text-primary"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
          >
            {currentFeature?.title}
          </motion.h3>
          <motion.p 
            key={`desc-${activeIndex}`}
            className="text-lg lg:text-xl text-muted-foreground leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
          >
            {currentFeature?.description}
          </motion.p>
        </motion.div>
      </div>

      {/* Scrollable Content / Triggers */}
      <div className="container mx-auto px-4 relative z-0">
        {featuresContent.map((feature, index) => (
          <motion.div
            key={feature.id}
            ref={el => { featureRefs.current[index] = el; }}
            className="min-h-[70vh] md:min-h-[90vh] py-12 md:py-24 flex flex-col justify-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: index * 0.1 }}
          >
            {/* Mobile View */}
            <motion.div 
              className="md:hidden mb-12 p-4 bg-inherit rounded-lg shadow-lg"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <motion.div 
                className="relative aspect-[16/10] rounded-lg overflow-hidden shadow-xl mb-6"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Image
                  src={feature.imageSrc}
                  alt={feature.title}
                  fill
                  className="object-contain"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </motion.div>
              <motion.h3 
                className="text-2xl font-bold mb-3 text-primary"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                {feature.title}
              </motion.h3>
              <motion.p 
                className="text-md text-muted-foreground"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                {feature.description}
              </motion.p>
            </motion.div>

            {/* Desktop View: Content for scroll triggering */}
            <div className="hidden md:flex justify-end items-center">
              <div className="md:w-1/2 lg:w-7/12 p-4 lg:p-8 rounded-lg">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-transparent">
                  {feature.title}
                </h2>
                <p className="text-lg lg:text-xl text-transparent leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <>
      <div className="absolute top-0 z-[-2] h-screen w-screen bg-neutral-950 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]"></div>
      <Suspense fallback={null}>
        <UtmCaptureHandler />
      </Suspense>

      {/* Sticky Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-border/40 opacity-90">
        <div className="w-full flex items-center justify-between px-4 py-3">
          <Link href="/" className="flex items-center">
            <Image
              src="/ss/LogoLarge.svg"
              alt="Trackr Logo"
              width={200}
              height={120}
              priority
            />
          </Link>
          <Button variant="ghost" asChild>
            <Link href="/auth">Log In</Link>
          </Button>
        </div>
      </header>

      {/* Main Content with a Top Padding to Offset the Fixed Header */}
      <main className="min-h-screen pt-20">
        {/* Hero Section */}
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            AI-Powered Insights for
              <span className="text-primary block">Smarter Recruiting</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Analyze your performance, uncover your potential, and connect with collegiate programs that match your athletic journey.
            </p>
            <div className="flex gap-4 justify-center">
              <Button size="lg" asChild>
                <Link href="/auth">
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#video">Learn More</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Marquee Section */}
        <div className="w-full overflow-x-hidden pt-8 pb-10">
          <div className="flex animate-marquee-mobile md:animate-marquee whitespace-nowrap">
            {/* First set of items */}
            <span className="mx-4 text-2xl font-bold italic text-slate-200">50 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">500 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">1650 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Back</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Back</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Breast</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Breast</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Fly</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Fly</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 IM</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">400 IM</span>
            
            {/* Second set of items (identical for seamless loop) */}
            <span className="mx-4 text-2xl font-bold italic text-slate-200">50 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">500 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">1650 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Back</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Back</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Breast</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Breast</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Fly</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Fly</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 IM</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">400 IM</span>

            {/* Third set of items (for extra buffer) */}
            <span className="mx-4 text-2xl font-bold italic text-slate-200">50 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">500 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">1650 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Back</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Back</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Breast</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Breast</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Fly</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Fly</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 IM</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">400 IM</span>

            {/* Fourth set of items (for extra buffer) */}
            <span className="mx-4 text-2xl font-bold italic text-slate-200">50 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">500 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">1650 Free</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Back</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Back</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Breast</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Breast</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">100 Fly</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 Fly</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">200 IM</span>
            <span className="mx-4 text-2xl font-bold italic text-slate-200">400 IM</span>
          </div>
        </div>

        {/* Vimeo Video Section */}
        <section id="video" className="w-full px-0 py-0">
          <div className="relative w-full aspect-[2/1] shadow-lg overflow-hidden">
            <iframe
              src="https://player.vimeo.com/video/1100355139?h=62bfdb3d92&background=1"
              title="Vimeo Video"
              className="absolute top-0 left-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </section>

        {/* Features Section - Replaced */}
        <InteractiveFeaturesDisplay />

        {/* Call-to-Action Section */}
        <section className="py-20 md:py-32">
          <div className="container mx-auto px-4 text-center max-w-4xl">
            <h2 className="text-5xl md:text-7xl font-bold tracking-tight mb-12 text-white leading-tight">
              Start Your College Journey Today
            </h2>
            <div className="flex gap-4 justify-center">
              <Button size="lg" className="px-8 py-4 text-lg font-semibold" asChild>
                <Link href="/auth">
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Footer Section */}
        <footer className="py-6">
          <div className="container mx-auto px-4 text-center">
            <div className="flex flex-col md:flex-row justify-center gap-4">
              <p className="text-muted-foreground mb-4">
                Contact us at{" "}
                <a href="mailto:contact@trackr.run" className="text-primary">
                  contact@trackr.run
                </a>
              </p>
              <div className="flex justify-center gap-4">
                <Link href="/legal" className="text-muted-foreground hover:text-primary">
                  Terms
                </Link>
                <Link href="/legal" className="text-muted-foreground hover:text-primary">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <p className="text-muted-foreground mt-4">
              © 2025 Trackr.run. Operated by TRACKR RUN LLC
            </p>
          </div>
        </footer>
      </main>
      <Script src="https://player.vimeo.com/api/player.js" strategy="lazyOnload" />
    </>
  );
}